<template>
    <main class="page page-dashboard">
        <div class="container">

            <PageHeader
                :title="$translate.text('clients.connections.add')"
            />

            <form @submit.prevent="submitForm" method="POST">
                <h4>{{ t('clients.connections.general') }}</h4>

                <YesNoInput
                    id="activated"
                    :value="connection.active ? 'yes' : 'no'"
                    name="activated"
                    @change="connection.active = $event === 'yes'"
                    :label="$translate.text('clients.connections.active')"
                />

                <YesNoInput
                    id="dryrun"
                    :value="connection.dryrun ? 'yes' : 'no'"
                    name="activated"
                    @change="connection.dryrun = $event === 'yes'"
                    :label="$translate.text('clients.connections.dryrun')"
                />

                <TextInput
                    id="name"
                    :value="connection.name"
                    name="name"
                    @change="connection.name = $event"
                    :label="$translate.text('clients.connections.name')"
                    :required="true"
                />

                <Select
                    id="webshop"
                    :value="connection.webshop_id"
                    name="webshop"
                    @change="connection.webshop_id = parseInt($event, 10)"
                    :options="webshopsSelectValues"
                    :label="$translate.text('clients.connections.webshop')"
                    :required="true"
                    :loading="!webshopsLoaded"
                />

                <TextInput
                    id="store_view"
                    :value="connection.store_view"
                    name="store_view"
                    @change="connection.store_view = $event"
                    :label="$translate.text('clients.connections.store_view')"
                />

                <Select
                    id="feed"
                    :value="connection.feed_id"
                    name="feed"
                    @change="connection.feed_id = parseInt($event, 10)"
                    :options="feedsSelectValues"
                    :label="$translate.text('clients.connections.feed')"
                    :required="true"
                    :loading="!feedsLoaded"
                />

                <Select
                    id="configbase"
                    :value="connection.configbase"
                    name="configbase"
                    @change="connection.configbase = $event"
                    :options="configbaseSelectValues"
                    :label="$translate.text('clients.connections.configbase')"
                    :required="true"
                />

                <h4>{{ t('clients.connections.sync_settings') }}</h4>

                <Select
                    id="update_interval"
                    :value="connection.update_interval"
                    name="update_interval"
                    @change="connection.update_interval = $event"
                    :options="update_intervalValues"
                    :label="$translate.text('general.update_interval')"
                    :required="true"
                />

                <Select
                    id="sync_interval"
                    :value="connection.sync_interval"
                    name="sync_interval"
                    @change="connection.sync_interval = $event"
                    :options="sync_intervalValues"
                    :label="$translate.text('general.sync_interval')"
                    :required="true"
                />

                <TextInput
                    id="name"
                    :value="connection.prefix"
                    name="name"
                    @change="connection.prefix = $event"
                    :label="$translate.text('clients.connections.prefix')"
                    :required="false"
                />

                <h4 v-if="showVariantsField">{{ t('clients.connections.variants') }}</h4>

                <YesNoInput
                    v-if="showVariantsField"
                    id="useVariants"
                    :value="connection.use_variants ? 'yes' : 'no'"
                    name="useVariants"
                    @change="connection.use_variants = $event === 'yes'"
                    :label="$translate.text('clients.connections.use_variants')"
                />

                <Select
                    v-if="connection.use_variants === true"
                    id="variant_match_field"
                    :value="connection.variant_match_field"
                    name="variant_match_field"
                    @change="connection.variant_match_field = $event"
                    :options="variantMatchFieldValues"
                    :label="$translate.text('clients.connections.variant_match_field')"
                    :required="true"
                    :loading="!feedsLoaded"
                />

                <h4>{{ t('clients.connections.vat_settings') }}</h4>

                <YesNoInput
                    id="vatInclude"
                    :value="connection.vat_include ? 'yes' : 'no'"
                    name="vatInclude"
                    @change="connection.vat_include = $event === 'yes'"
                    :label="$translate.text('clients.connections.vat_include')"
                    :disabled="selectedWebshopType === 'Shopware6'"
                />

                <YesNoInput
                    id="vatCalculate"
                    :value="connection.vat_calculate ? 'yes' : 'no'"
                    name="vatCalculate"
                    @change="connection.vat_calculate = $event === 'yes'"
                    :label="$translate.text('clients.connections.vat_calculate')"
                />

                <Select
                    v-if="connection.vat_calculate === true"
                    id="vatPercentage"
                    :value="connection.vat_percentage"
                    name="vatPercentage"
                    @change="connection.vat_percentage = $event"
                    :options="vatOptions"
                    :label="$translate.text('clients.connections.vat_percentage')"
                    :required="true"
                />

                <h4>{{ t('clients.connections.seo.settings') }}</h4>

                <YesNoInput
                    id="seoActive"
                    :value="connection.seo.active ? 'yes' : 'no'"
                    name="seoActive"
                    @change="connection.seo.active = $event === 'yes'"
                    :label="$translate.text('clients.connections.seo.active')"
                />

                <h4>{{ t('clients.connections.provider.settings') }}</h4>

                <YesNoInput
                    id="vatContentProvider"
                    :value="connection.content_provider.active ? 'yes' : 'no'"
                    name="vatContentProvider"
                    @change="connection.content_provider.active = $event === 'yes'"
                    :label="$translate.text('clients.connections.provider.active')"
                />

                <Select
                    v-if="connection.content_provider.active === true"
                    id="connectionProvider"
                    :value="connection.content_provider.provider"
                    name="connectionProvider"
                    @change="connection.content_provider.provider = $event"
                    :options="providers"
                    :label="$translate.text('clients.connections.provider')"
                />

                <div class="form-group" v-if="connection.content_provider.provider === 'icecat' && connection.content_provider.active === true">
                    <div class="info-bar">
                        {{ t('clients.connections.provider.infobar.icecat')}}
                    </div>
                </div>

                <Select
                    v-if="connection.content_provider.provider === 'icecat' && connection.content_provider.active === true"
                    id="ContentProviderLanguage"
                    :value="connection.content_provider.language"
                    name="ContentProviderLanguage"
                    @change="connection.content_provider.language = $event"
                    :options="contentProviderLanguages.icecat"
                    :label="$translate.text('general.language')"
                />

                <div class="form-group" v-if="connection.content_provider.provider === 'icecat' && connection.content_provider.active === true">
                    <div class="info-bar">
                        {{ t('clients.connections.provider.infobar.language')}}
                    </div>
                </div>

                <TextInput
                    id="providerUsername"
                    v-if="connection.content_provider.active === true"
                    :value="connection.content_provider.username"
                    name="providerUsername"
                    @change="connection.content_provider.username = $event"
                    :label="$translate.text('clients.connections.provider.username')"
                />

                <PasswordInput
                    v-if="connection.content_provider.active === true"
                    id="providerPw"
                    :value="connection.content_provider.password"
                    name="providerPw"
                    @change="connection.content_provider.password = $event"
                    :label="$translate.text('clients.connections.provider.password')"
                />

                <h4>{{ t('clients.connections.notifications') }}</h4>

                <div class="form-group">
                    <label for="notification_email">{{ $translate.text('clients.connections.notifications.administrative') }}
                        <i
                            style="font-size: 14px;"
                        ></i>
                    </label>

                    <Multiselect
                        v-model="connection.notification_email"
                        :options="[]"
                        :multiple="true"
                        :taggable="true"
                        @tag="addEmail"
                        placeholder="Add new email"
                        label="email"
                        track-by="email"
                        open-direction="bottom"
                    >
                    </Multiselect>
                </div>

                <h4>{{ t('clients.connections.labels.settings') }}</h4>

                <TextInput
                    id="label_related"
                    :value="connection.label_related"
                    name="label_related"
                    @change="connection.label_related = $event"
                    :placeholder="`Related`"
                    :label="$translate.text('clients.connections.labels.label_related')"
                />

                <TextInput
                    id="label_cross_sell"
                    :value="connection.label_cross_sell"
                    name="label_cross_sell"
                    @change="connection.label_cross_sell = $event"
                    :placeholder="`Similar`"
                    :label="$translate.text('clients.connections.labels.label_cross_sell')"
                />

<!--                <h4>{{ t('clients.connections.stock.settings') }}</h4>-->

<!--                <TextInput-->
<!--                    id="restock_time"-->
<!--                    :value="connection.restock_time"-->
<!--                    name="restock_time"-->
<!--                    @change="connection.restock_time = $event"-->
<!--                    :label="$translate.text('clients.connections.stock.restock_time')"-->
<!--                />-->

<!--                <YesNoInput-->
<!--                    id="stockClearanceSale"-->
<!--                    :value="connection.stock.clearance_sale ? 'yes' : 'no'"-->
<!--                    name="stockClearanceSale"-->
<!--                    @change="connection.stock.clearance_sale = $event === 'yes'"-->
<!--                    :label="$translate.text('clients.connections.stock.clearance_sale')"-->
<!--                />-->

<!--                <Select-->
<!--                    v-if="deliverytimesLoaded"-->
<!--                    id="stockDeliveryTime"-->
<!--                    :value="connection.stock.delivery_time"-->
<!--                    name="stockDeliveryTime"-->
<!--                    @change="connection.stock.delivery_time = $event"-->
<!--                    :options="deliverytimesSelectValues"-->
<!--                    :label="$translate.text('clients.connections.stock.delivery_time')"-->
<!--                />-->

                <FormSubmit
                    :routeAfterCancelLink="{ name: 'Clients Connections' }"
                    :buttonLabel="submittingIsLoading ? $translate.text('general.save.saving') :  $translate.text('general.save')"
                    :disabled="submittingIsLoading"
                />
            </form>
        </div>
    </main>
</template>
<script>
import Multiselect from 'vue-multiselect';
import handleError from '@/helpers/error';

import PageHeader from '@/components/PageHeader/PageHeader';
import TextInput from '@/components/Inputs/TextInput';
import PasswordInput from '@/components/Inputs/PasswordInput';
import YesNoInput from '@/components/Inputs/YesNoInput';
import Select from '@/components/Inputs/Select';
import FormSubmit from '@/components/Forms/FormSubmit';


export default {
    name: 'ClientAddConnection',

    components: {
        PageHeader,
        TextInput,
        PasswordInput,
        YesNoInput,
        Select,
        FormSubmit,
        Multiselect,
    },

    data() {
        return {
            connection: {
                name: '',
                active: true,
                dryrun: false,
                webshop_id: null,
                store_view: 0,
                feed_id: null,
                configbase: 'feed',
                use_variants: false,
                variant_match_field: 'variant',
                vat_include: false,
                vat_calculate: false,
                vat_percentage: 0,
                notification_email: [],
                sync_interval: 24 * 30,
                update_interval: 24 * 14,
                prefix: '',
                seo: {
                    active: false,
                },
                content_provider: {
                    active: false,
                    provider: null,
                    language: null,
                    username: '',
                    password: '',
                },
                stock: {
                    restock_time: null,
                    clearance_sale: false,
                    delivery_time: null,
                },
                interval: 1,
                label_related: '',
                label_cross_sell: '',
            },

            configbaseSelectValues: [
                { label: this.$translate.text('clients.connections.configbase.feed'), value: 'feed' },
                { label: this.$translate.text('clients.connections.configbase.webshop'), value: 'webshop' },
            ],

            variantMatchFieldValues: [
                { label: this.$translate.text('client.feeds.combine.columns.variant'), value: 'variant' },
                { label: this.$translate.text('client.feeds.combine.columns.name'), value: 'name' },
                { label: this.$translate.text('client.feeds.combine.columns.unique_code'), value: 'unique_code' },
                { label: this.$translate.text('client.feeds.combine.columns.manufacturer_code'), value: 'manufacturer_code' },
            ],

            sync_intervalValues: [
                { label: this.$translate.text('general.none'), value: 0 },
                { label: `1 ${this.$translate.text('general.day')}`, value: 24 * 1 },
                { label: `2 ${this.$translate.text('general.day')}`, value: 24 * 2 },
                { label: `3 ${this.$translate.text('general.day')}`, value: 24 * 3 },
                { label: `4 ${this.$translate.text('general.day')}`, value: 24 * 4 },
                { label: `5 ${this.$translate.text('general.day')}`, value: 24 * 5 },
                { label: `6 ${this.$translate.text('general.day')}`, value: 24 * 6 },
                { label: `1 ${this.$translate.text('general.week')}`, value: 24 * 7 },
                { label: `2 ${this.$translate.text('general.week')}`, value: 24 * 14 },
                { label: `1 ${this.$translate.text('general.month')}`, value: 24 * 30 },
            ],

            update_intervalValues: [
                { label: this.$translate.text('general.none'), value: 0 },
                { label: `1 ${this.$translate.text('general.hour')}`, value: 1 },
                { label: `4 ${this.$translate.text('general.hour')}`, value: 4 },
                { label: `8 ${this.$translate.text('general.hour')}`, value: 8 },
                { label: `12 ${this.$translate.text('general.hour')}`, value: 12 },
                { label: `13 ${this.$translate.text('general.hour')}`, value: 13 },
                { label: `1 ${this.$translate.text('general.day')}`, value: 24 * 1 },
                { label: `2 ${this.$translate.text('general.day')}`, value: 24 * 2 },
                { label: `3 ${this.$translate.text('general.day')}`, value: 24 * 3 },
                { label: `4 ${this.$translate.text('general.day')}`, value: 24 * 4 },
                { label: `5 ${this.$translate.text('general.day')}`, value: 24 * 5 },
                { label: `6 ${this.$translate.text('general.day')}`, value: 24 * 6 },
                { label: `1 ${this.$translate.text('general.week')}`, value: 24 * 7 },
                { label: `2 ${this.$translate.text('general.week')}`, value: 24 * 14 },
            ],

            vatOptions: [
                { label: this.$translate.text('general.select_option'), value: null, disabled: true },
                { label: '0%', value: 0 },
                { label: '6%', value: 6 },
                { label: '9%', value: 9 },
                { label: '19%', value: 19 },
                { label: '21%', value: 21 },
            ],

            providers: [
                { label: this.$translate.text('general.select_option'), value: null, disabled: true },
                { label: 'IceCat', value: 'icecat' },
            ],

            contentProviderLanguages: {
                icecat: [
                    { label: this.$translate.text('general.select_option'), value: null, disabled: true },
                    { label: this.$translate.text('icecat.languages.en'), value: 'en' },
                    { label: this.$translate.text('icecat.languages.nl'), value: 'nl' },
                    { label: this.$translate.text('icecat.languages.fr'), value: 'fr' },
                    { label: this.$translate.text('icecat.languages.de'), value: 'de' },
                    { label: this.$translate.text('icecat.languages.it'), value: 'it' },
                    { label: this.$translate.text('icecat.languages.es'), value: 'es' },
                    { label: this.$translate.text('icecat.languages.dk'), value: 'dk' },
                    { label: this.$translate.text('icecat.languages.ru'), value: 'ru' },
                    { label: this.$translate.text('icecat.languages.us'), value: 'us' },
                    { label: this.$translate.text('icecat.languages.br'), value: 'br' },
                    { label: this.$translate.text('icecat.languages.pt'), value: 'pt' },
                    { label: this.$translate.text('icecat.languages.zh'), value: 'zh' },
                    { label: this.$translate.text('icecat.languages.sv'), value: 'sv' },
                    { label: this.$translate.text('icecat.languages.pl'), value: 'pl' },
                    { label: this.$translate.text('icecat.languages.cz'), value: 'cz' },
                    { label: this.$translate.text('icecat.languages.hu'), value: 'hu' },
                    { label: this.$translate.text('icecat.languages.fi'), value: 'fi' },
                    { label: this.$translate.text('icecat.languages.el'), value: 'el' },
                    { label: this.$translate.text('icecat.languages.no'), value: 'no' },
                    { label: this.$translate.text('icecat.languages.tr'), value: 'tr' },
                    { label: this.$translate.text('icecat.languages.bg'), value: 'bg' },
                    { label: this.$translate.text('icecat.languages.ka'), value: 'ka' },
                    { label: this.$translate.text('icecat.languages.ro'), value: 'ro' },
                    { label: this.$translate.text('icecat.languages.sr'), value: 'sr' },
                    { label: this.$translate.text('icecat.languages.uk'), value: 'uk' },
                    { label: this.$translate.text('icecat.languages.ja'), value: 'ja' },
                    { label: this.$translate.text('icecat.languages.ca'), value: 'ca' },
                    { label: this.$translate.text('icecat.languages.es_ar'), value: 'es_ar' },
                    { label: this.$translate.text('icecat.languages.hr'), value: 'hr' },
                    { label: this.$translate.text('icecat.languages.ar'), value: 'ar' },
                    { label: this.$translate.text('icecat.languages.vi'), value: 'vi' },
                    { label: this.$translate.text('icecat.languages.ko'), value: 'ko' },
                    { label: this.$translate.text('icecat.languages.mk'), value: 'mk' },
                    { label: this.$translate.text('icecat.languages.sl'), value: 'sl' },
                    { label: this.$translate.text('icecat.languages.en_sg'), value: 'en_sg' },
                    { label: this.$translate.text('icecat.languages.en_za'), value: 'en_za' },
                    { label: this.$translate.text('icecat.languages.zh_tw'), value: 'zh_tw' },
                    { label: this.$translate.text('icecat.languages.he'), value: 'he' },
                    { label: this.$translate.text('icecat.languages.lt'), value: 'lt' },
                    { label: this.$translate.text('icecat.languages.lv'), value: 'lv' },
                    { label: this.$translate.text('icecat.languages.en_in'), value: 'en_in' },
                    { label: this.$translate.text('icecat.languages.de_ch'), value: 'de_ch' },
                    { label: this.$translate.text('icecat.languages.id'), value: 'id' },
                    { label: this.$translate.text('icecat.languages.sk'), value: 'sk' },
                    { label: this.$translate.text('icecat.languages.fa'), value: 'fa' },
                    { label: this.$translate.text('icecat.languages.ex_mx'), value: 'ex_mx' },
                    { label: this.$translate.text('icecat.languages.et'), value: 'et' },
                    { label: this.$translate.text('icecat.languages.de_be'), value: 'de_be' },
                    { label: this.$translate.text('icecat.languages.fr_be'), value: 'fr_be' },
                    { label: this.$translate.text('icecat.languages.nl_be'), value: 'nl_be' },
                    { label: this.$translate.text('icecat.languages.th'), value: 'th' },
                ],
            },

            deliverytimes: [],
            deliverytimesLoaded: false,

            webshops: [],
            webshopsLoaded: false,
            feeds: [],
            feedsLoaded: false,

            submittingIsLoading: false,
        };
    },

    created() {
        this.getWebshops();
        this.getFeeds();
        // this.getDeliverytimes();
    },

    methods: {

        addEmail(newEmail) {
            const email = {
                email: newEmail,
            };
            this.connection.notification_email.push(email);
        },

        getWebshops() {
            this.API.clients.webshops.index()
                .then(response => {
                    this.webshops = response.data.data;
                    this.webshopsLoaded = true;
                }).catch(error => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        getFeeds() {
            this.API.clients.feeds.index()
                .then(response => {
                    this.feeds = response.data.data;
                    this.feedsLoaded = true;
                }).catch(error => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        getDeliverytimes() {
            this.API.clients.connections.deliverytimes(this.$route.params.id)
                .then(response => {
                    this.deliverytimes = response.data.data;
                    this.deliverytimesLoaded = true;
                }).catch(error => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        submitForm() {
            this.submittingIsLoading = true;

            const submitData = { ...this.connection };
            // Create simple array
            submitData.notification_email = submitData.notification_email.map(item => item.email);

            this.API.clients.connections.store(submitData)
                .then(() => {
                    this.submittingIsLoading = false;
                    this.$router.push({ name: 'Clients Connections' });
                    this.resetForm();
                })
                .catch((error) => {
                    this.submittingIsLoading = false;
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        resetForm() {
            this.connection = {
                name: '',
                active: true,
                dryrun: false,
                webshop_id: null,
                store_view: 0,
                feed_id: null,
                configbase: 'feed',
                use_variants: false,
                variant_match_field: 'variant',
                vat_include: false,
                vat_calculate: false,
                vat_percentage: 0,
                notification_email: [],
                sync_interval: 24 * 30,
                update_interval: 24 * 14,
                prefix: '',
                seo: {
                    active: false,
                },
                content_provider: {
                    active: false,
                    provider: null,
                    language: null,
                    username: '',
                    password: '',
                },
                stock: {
                    restock_time: null,
                    clearance_sale: false,
                    delivery_time: null,
                },
                interval: 1,
                label_related: '',
                label_cross_sell: '',
            };
        },
    },

    computed: {
        showVariantsField() {
            if (this.connection.webshop_id === null) {
                return true;
            }

            const webshop = this.webshops.find(item => item.id === this.connection.webshop_id);

            if (this.webshops.length > 0 && (webshop.type === 'Shopware5' || webshop.type === 'Shopware6' || webshop.type === 'Magento' || webshop.type === 'WooCommerce')) {
                return true;
            }

            return false;
        },

        webshopsSelectValues() {
            const values = [{ label: this.$translate.text('general.select_option'), value: null, disabled: true }];

            this.webshops.forEach(webshop => {
                values.push({
                    value: parseInt(webshop.id, 10),
                    label: webshop.name,
                });
            });

            return values;
        },

        feedsSelectValues() {
            const values = [{ label: this.$translate.text('general.select_option'), value: null, disabled: true }];

            this.feeds.forEach(feed => {
                values.push({
                    value: parseInt(feed.id, 10),
                    label: feed.name,
                });
            });

            return values;
        },

        deliverytimesSelectValues() {
            const values = [{ label: this.$translate.text('general.select_option'), value: null, disabled: true }];

            this.deliverytimes.forEach(deliverytime => {
                values.push({
                    value: deliverytime.id,
                    label: deliverytime.name,
                });
            });

            return values;
        },

        selectedWebshopType() {
            let type = null;

            this.webshops.forEach(webshop => {
                if (webshop.id === this.connection.webshop_id) {
                    type = webshop.type;
                }
            });

            return type;
        },
    },

    watch: {
        selectedWebshopType(e) {
            if (e === 'Shopware6') {
                this.connection.vat_include = true;
            }
        },
    },


};
</script>
<style lang="scss" scoped>

    .form-group {
        display: flex;
        align-items: center;

        .info-bar {
            margin-left: 270px;
            background: rgba($blue, .1);
            border: 1px solid $blue;
            padding: 10px;
            font-size: $font-size-base * 0.8;
            border-radius: 5px;
            width: 100%;
        }

        label {
            color: $black;
            flex: 2;
        }

        .multiselect {
            -webkit-box-flex: 6;
        -ms-flex: 6;
        flex: 6;
        // height: 42px;
        font-size: 16px;
        border: 0;
        background-color: white;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        cursor: pointer;
        padding: 0px 16px;
        }
    }

</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css">

</style>
